import {useCallback, useEffect, useRef, useState} from 'react'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {COPILOT_REPOSITORIES_API_ENDPOINT} from '../utils/constants'

export interface DropdownRepository {
  id: number
  name: string // This will be nameWithOwner for display in ReposSelector
  nameOnly: string // This will be the original repository name (without owner)
  nameWithOwner: string
  owner: string
  enabled: boolean
  defaultBranch: string
  private: boolean
}

export interface SimpleRepository {
  databaseId?: number
  name: string
  owner: {
    login: string
  }
  isPrivate?: boolean
}

/**
 * Shared function to fetch Copilot-enabled repositories from the API
 */
export async function queryForRepos(query: string = ''): Promise<DropdownRepository[]> {
  const url = query.trim()
    ? `${COPILOT_REPOSITORIES_API_ENDPOINT}?q=${encodeURIComponent(query.trim())}`
    : COPILOT_REPOSITORIES_API_ENDPOINT
  const response = await verifiedFetchJSON(url)
  if (response.ok) {
    let repos = (await response.json()).repositories.map(
      (repo: {
        id: number
        nameWithOwner: string
        enabled: boolean
        name: string
        owner: string
        defaultBranch: string
        visibility: string
      }) => {
        return {
          id: repo.id,
          nameWithOwner: repo.nameWithOwner,
          enabled: repo.enabled,
          name: repo.nameWithOwner,
          nameOnly: repo.name,
          owner: repo.owner,
          defaultBranch: repo.defaultBranch,
          private: repo.visibility !== 'public',
        }
      },
    )

    // Apply client-side filtering to search both repository name and nameWithOwner
    if (query.trim()) {
      const lowerQuery = query.toLowerCase()
      repos = repos.filter(
        (repo: DropdownRepository) =>
          repo.nameWithOwner.toLowerCase().includes(lowerQuery) || repo.nameOnly.toLowerCase().includes(lowerQuery),
      )
    }

    return repos
  }
  return []
}

/**
 * Hook to manage repository fetching and selection logic for Copilot assignment forms
 */
export function useCopilotRepositoryFetching(initialRepo?: SimpleRepository | null) {
  const [selectedRepo, setSelectedRepo] = useState<DropdownRepository | null>(null)
  const [selectedBranch, setSelectedBranch] = useState<string>('')
  const [isLoadingRepos, setIsLoadingRepos] = useState(true)
  const [isLoadingBranch, setIsLoadingBranch] = useState(false)

  // Use a ref to track and cancel pending default branch requests
  const abortControllerRef = useRef<AbortController | null>(null)

  // Initialize repo from props — only if user hasn't picked a repo yet
  useEffect(() => {
    if (!initialRepo || selectedRepo) return

    const nameWithOwner = `${initialRepo.owner.login}/${initialRepo.name}`
    setSelectedRepo({
      id: initialRepo.databaseId || 0,
      owner: initialRepo.owner.login,
      name: nameWithOwner,
      nameOnly: initialRepo.name,
      nameWithOwner,
      enabled: true,
      defaultBranch: '',
      private: initialRepo.isPrivate || false,
    })

    // Fetch the default branch from the server async
    const fetchDefaultBranch = async () => {
      try {
        setIsLoadingBranch(true)

        // Cancel any previous request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort()
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController()
        const currentAbortController = abortControllerRef.current

        const repos = await queryForRepos(nameWithOwner)

        // Check if request was aborted before processing results
        if (currentAbortController.signal.aborted) {
          return
        }

        if (!repos) return
        const repo = repos.find((repoData: {nameWithOwner: string}) => repoData.nameWithOwner === nameWithOwner)
        if (repo) {
          setSelectedBranch(repo.defaultBranch)
          setSelectedRepo(prev => (prev ? {...prev, defaultBranch: repo.defaultBranch} : prev))
        }
      } catch (error) {
        // Don't show errors for aborted requests
        if (error instanceof Error && error.name === 'AbortError') {
          return
        }
        // Handle other errors if needed
      } finally {
        // Only update loading state if this request wasn't aborted
        if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
          setIsLoadingBranch(false)
        }
      }
    }

    fetchDefaultBranch()
  }, [initialRepo, selectedRepo])

  // Load available repositories
  useEffect(() => {
    const fetchRepos = async () => {
      try {
        setIsLoadingRepos(true)
        await queryForRepos() // Just ensure endpoint is accessible
      } finally {
        setIsLoadingRepos(false)
      }
    }

    fetchRepos()
  }, [])

  // Cleanup: Cancel any pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const handleRepoSelect = useCallback((repo: DropdownRepository | undefined) => {
    if (!repo) return

    // Cancel any pending default branch request when user selects a new repo
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Reset loading state since we're setting the branch immediately from repo.defaultBranch
    setIsLoadingBranch(false)
    setSelectedRepo(repo)
    setSelectedBranch(repo.defaultBranch)
  }, [])

  return {
    selectedRepo,
    selectedBranch,
    isLoadingRepos,
    isLoadingBranch,
    setSelectedBranch,
    handleRepoSelect,
  }
}
